import {
  createContext,
  createMemo,
  useContext,
  type Accessor,
  type JSX,
} from 'solid-js';

/**
 * Context for providing a custom mount target for SolidJS Portals.
 *
 * The value is an **accessor** so consumers stay subscribed when the mount
 * node appears after the first paint (e.g. `ref` on a wrapper div). Passing a
 * plain `HTMLElement | undefined` snapshot would freeze `undefined` forever.
 *
 * In Web Component mode, portals must mount inside the custom element
 * so that scoped CSS styles apply to portal content (tooltips, dropdowns, modals).
 *
 * In standalone app mode (no provider), returns an accessor that always yields
 * `undefined`, which causes Portal to use its default mount target (`document.body`).
 */
const PortalMountContext = createContext<Accessor<HTMLElement | undefined>>(
  () => undefined
);

/**
 * Provider component for the portal mount context.
 *
 * @example
 * ```tsx
 * // Reactive mount (recommended when the node comes from a ref / signal):
 * const [portalMount, setPortalMount] = createSignal<HTMLElement | undefined>();
 * <div ref={setPortalMount}>
 *   <PortalMountProvider mount={() => portalMount()}>
 *     <App />
 *   </PortalMountProvider>
 * </div>
 *
 * // Web component host (stable element):
 * <PortalMountProvider mount={this}>
 *   <App />
 * </PortalMountProvider>
 * ```
 *
 * @param props.mount - Accessor, getter, or element to use as the portal mount target.
 *   For a `createSignal` accessor, pass `() => portalMount()` so each read tracks updates.
 * @param props.children - Child components that may use portals
 */
export function PortalMountProvider(props: {
  mount: Accessor<HTMLElement | undefined> | HTMLElement | undefined;
  children: JSX.Element;
}): JSX.Element {
  const value = createMemo(() => {
    const m = props.mount;
    if (typeof m === 'function') {
      return (m as Accessor<HTMLElement | undefined>)();
    }
    return m as HTMLElement | undefined;
  });

  return (
    <PortalMountContext.Provider value={value}>
      {props.children}
    </PortalMountContext.Provider>
  );
}

/**
 * Hook to get the portal mount target from context.
 *
 * @returns An accessor for the current portal mount target element.
 *          Returns `undefined` when no provider exists (standalone app mode)
 *          or while mount is not ready, causing Portal to use `document.body`.
 */
export function usePortalMount(): Accessor<HTMLElement | undefined> {
  return useContext(PortalMountContext);
}
